import React from 'react';
import { Badge } from '@global-ecom/nitro-uds/elements';

import { useTranslate } from 'hooks/useTranslations';
import { useFeature } from 'hooks/useFeature';

export enum STOCK_MESSAGE_CODE {
  IN_STOCK = 'IN_STOCK',
  LIMITED_STOCK = 'LIMITED_STOCK',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
}

export const AvailabilityBadge: React.FC<{
  className?: string;
  outOfStock: boolean;
  offline: boolean;
  stockMessage?: string | undefined | null;
  stockStatus?: STOCK_MESSAGE_CODE;
}> = ({ className, outOfStock, offline, stockMessage, stockStatus }) => {
  const shouldUseStockMessageFromResponse = useFeature(
    'USE_STOCK_MESSAGE_FROM_RESPONSE'
  );
  const t = useTranslate();

  const isOutOfStock =
    outOfStock ||
    (shouldUseStockMessageFromResponse && stockStatus === 'NOT_AVAILABLE');

  const isLimitedStock =
    shouldUseStockMessageFromResponse && stockStatus === 'LIMITED_STOCK';

  const hasStockMessage =
    shouldUseStockMessageFromResponse && stockMessage ? true : false;

  const getBadgeLabel = (): string => {
    switch (true) {
      case hasStockMessage:
        return stockMessage as string;

      case isOutOfStock:
        return t('outOfStock');

      case offline:
        return t('noLongerAvailable');

      case isLimitedStock:
        return t('inStock');

      default:
        return t('inStock');
    }
  };

  const getBadgeColor = () => {
    switch (true) {
      case isOutOfStock:
        return 'brand';

      case offline:
        return 'muted';

      case isLimitedStock:
        return 'warning';

      default:
        return 'success';
    }
  };

  const getBadgeIcon = () => {
    switch (true) {
      case isOutOfStock:
        return 'close-filled';

      case offline:
        return 'close-filled';

      case isLimitedStock:
        return 'exclamation-filled';

      default:
        return 'check-filled';
    }
  };

  return (
    <Badge
      className={className}
      data-test-id="stock-availability"
      outline
      round
      size="sm"
      icon={getBadgeIcon()}
      color={getBadgeColor()}
      label={getBadgeLabel()}
    />
  );
};
